import { FlightDto, MonitoredLoadValue, RegulationOrMcdmOnlyDto, TimeRange } from '../../../../model/CORE';
import { TimeSlice, TimeSliceSubTotal } from './RegulationInteractionGraph';
import TimeRangeUtils from '../../../../common/util/TimeRangeUtils';
import TimePeriodUtils from '../../../../common/util/TimePeriodUtils';
import MeasureUtils from '../../../../common/util/MeasureUtils';

export interface DisplayData {
    timeRange: TimeRange;
    colors: Map<string, string>;
    timeSlices: Array<TimeSlice>;
}

export interface MeasureLoadData {
    timeRange: TimeRange;
    loadValues: Array<MonitoredLoadValue>;
}

export class RegulationInteractionUtils {
    public static getRegulation(regulations: Array<RegulationOrMcdmOnlyDto>, regulId: string) {
        return regulations?.find(regulation => regulation.measureId.id === regulId);
    }

    /**
     * Return a count of the flights grouped by their most penalising regulation
     * @param timeRange
     * @param flights
     */
    public static getInfluencingTimeSlices(timeRange: TimeRange, flights: Array<FlightDto>): Array<TimeSlice> {
        const timeSlices = new Array<TimeSlice>();
        for (let i = 0; i < TimeRangeUtils.getNumberOfSteps(timeRange); i++) {
            const tp = TimeRangeUtils.getTimePeriodAtStep(timeRange, i);
            const map = new Map<string, TimeSliceSubTotal>();
            flights
                .filter(flight => TimePeriodUtils.contains(tp, flight.timeAtReferenceLocationEntry?.time))
                .forEach(flight => {
                    if (flight.mostPenalisingRegulation) {
                        let subTotal;
                        if (map.has(flight.mostPenalisingRegulation)) {
                            subTotal = map.get(flight.mostPenalisingRegulation);
                        } else {
                            subTotal = {
                                regulationId: flight.mostPenalisingRegulation,
                                nbFlights: 0,
                            };
                            map.set(flight.mostPenalisingRegulation, subTotal);
                        }
                        subTotal.nbFlights++;
                    }
                });

            timeSlices.push({
                timePeriod: tp,
                total: Array.from(map.values()).reduce((total, subTotal) => total + subTotal.nbFlights, 0),
                subTotals: Array.from(map.values()),
            });
        }
        return timeSlices;
    }

    /**
     * Return a count of the flights that are forced by the provided regulationId into other regulations.
     * @param regulId
     * @param timeRange
     * @param flights
     */
    public static getInfluencedTimeSlices(
        regulationId: string,
        timeRange: TimeRange,
        flights: Array<FlightDto>,
    ): Array<TimeSlice> {
        const timeSlices = new Array<TimeSlice>();
        for (let i = 0; i < TimeRangeUtils.getNumberOfSteps(timeRange); i++) {
            const tp = TimeRangeUtils.getTimePeriodAtStep(timeRange, i);
            const map = new Map<string, TimeSliceSubTotal>();
            flights
                .filter(
                    flight =>
                        flight.mostPenalisingRegulation === regulationId &&
                        TimePeriodUtils.contains(tp, flight.timeAtReferenceLocationEntry?.time),
                )
                .forEach(flight => {
                    if (flight.regulationLocations) {
                        flight.regulationLocations
                            .filter(regulationLocation => regulationLocation.regulationId !== regulationId)
                            .forEach(regulationLocation => {
                                let subTotal;
                                if (map.has(regulationLocation.regulationId)) {
                                    subTotal = map.get(regulationLocation.regulationId);
                                } else {
                                    subTotal = {
                                        regulationId: regulationLocation.regulationId,
                                        nbFlights: 0,
                                    };
                                    map.set(regulationLocation.regulationId, subTotal);
                                }
                                subTotal.nbFlights++;
                            });
                    }
                });

            timeSlices.push({
                timePeriod: tp,
                total: 0,
                subTotals: Array.from(map.values()),
            });
        }
        return timeSlices;
    }

    public static getMeasureLoadValues(
        regulationId: string,
        timeRange: TimeRange,
        flights: Array<FlightDto>,
    ): Array<MonitoredLoadValue> {
        const timeSlices = new Array<MonitoredLoadValue>();
        for (let i = 0; i < TimeRangeUtils.getNumberOfSteps(timeRange); i++) {
            const tp = TimeRangeUtils.getTimePeriodAtStep(timeRange, i);
            const delays = flights
                .filter(
                    flight =>
                        flight.mostPenalisingRegulation === regulationId &&
                        TimePeriodUtils.contains(tp, flight.timeAtReferenceLocationEntry?.time),
                )
                .map(flight => flight.delayInMs);
            const averageDelayMs = delays.reduce((sum, delayMs) => sum + delayMs, 0) / delays.length || 0;

            timeSlices.push({
                loadClassification: MeasureUtils.toLoadClassification(averageDelayMs),
                timePeriod: tp,
            });
        }
        return timeSlices;
    }
}
